import {
  AgentMessageEventGuardrailCheckData,
  AgentMessageEventGuardrailDataOutput,
  AgentMessageEventOutput,
  AgentMessageEventType,
  GuardrailCheckStatus,
  GuardrailType,
} from '../../../gamma-api';

const GUARDRAIL_EVENT_TYPE = AgentMessageEventType.Guardrail;

export type GuardrailEventData = AgentMessageEventGuardrailDataOutput;
export type GuardrailCheckData = AgentMessageEventGuardrailCheckData;

export enum GuardrailCheckedType {
  UserMessage = 'user_message',
  AgentMessage = 'agent_message',
  Message = 'message',
  ToolCall = 'tool_call',
  ToolResult = 'tool_result',
  History = 'history',
}

export type GuardrailCheckedItemType = Exclude<GuardrailCheckedType, GuardrailCheckedType.History>;

export interface GuardrailCheckedItem {
  type: GuardrailCheckedItemType;
  toolName?: string;
  isHistory: boolean;
}

export type GuardrailCheckedTargetType = GuardrailCheckedType;

export interface GuardrailCheckedTarget {
  type: GuardrailCheckedTargetType;
  toolName?: string;
}

interface GuardrailCheckInputEntry {
  type?: unknown;
  tool_name?: unknown;
}

export const isGuardrailEvent = (event: AgentMessageEventOutput): boolean =>
  event.data.event_type === GUARDRAIL_EVENT_TYPE;

export const isGuardrailCheckBlocked = (data: GuardrailCheckData): boolean =>
  data.status === GuardrailCheckStatus.Block;

export const getGuardrailCheckStatus = (data: GuardrailCheckData): GuardrailCheckStatus =>
  data.status || (isGuardrailCheckBlocked(data) ? GuardrailCheckStatus.Block : GuardrailCheckStatus.Pass);

export const isGuardrailCheckFailed = (data: GuardrailCheckData): boolean =>
  getGuardrailCheckStatus(data) === GuardrailCheckStatus.Error || Boolean(data.error_message?.trim());

export const isGuardrailCheckWarning = (data: GuardrailCheckData): boolean => {
  const status = getGuardrailCheckStatus(data);
  return status === GuardrailCheckStatus.Audit || (isGuardrailCheckFailed(data) && !isGuardrailCheckBlocked(data));
};

export const getGuardrailCheckTypeTitle = (data: GuardrailCheckData): string => {
  switch (data.guardrail_type) {
    case GuardrailType.CustomPromptCheck:
      return 'LLM';
    case GuardrailType.RegexCheck:
      return 'Regexp';
    case GuardrailType.ExternalCheck:
      return 'External';
    default:
      return data.guardrail_type;
  }
};

const isCheckedItemType = (value: unknown): value is GuardrailCheckedItemType =>
  value === GuardrailCheckedType.UserMessage ||
  value === GuardrailCheckedType.AgentMessage ||
  value === GuardrailCheckedType.Message ||
  value === GuardrailCheckedType.ToolCall ||
  value === GuardrailCheckedType.ToolResult;

const getCheckInputEntries = (value: unknown): GuardrailCheckInputEntry[] =>
  Array.isArray(value)
    ? value.filter((item): item is GuardrailCheckInputEntry => Boolean(item && typeof item === 'object'))
    : [];

const getToolName = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const normalizeCheckedItems = (value: unknown, isHistory: boolean): GuardrailCheckedItem[] => {
  const entries = getCheckInputEntries(value);
  const result: GuardrailCheckedItem[] = [];

  for (const entry of entries) {
    if (isCheckedItemType(entry.type)) {
      const precedingToolName = [...result].reverse().find((item) => item.type === GuardrailCheckedType.ToolCall)
        ?.toolName;
      result.push({
        type: entry.type,
        toolName:
          getToolName(entry.tool_name) ||
          (entry.type === GuardrailCheckedType.ToolResult ? precedingToolName : undefined),
        isHistory,
      });
    }
  }

  return result;
};

const getGuardrailCheckedItems = (data: GuardrailCheckData): GuardrailCheckedItem[] => {
  const checkInput = data.check_input;
  if (!checkInput || typeof checkInput !== 'object') {
    return [];
  }

  return [
    ...normalizeCheckedItems(checkInput.current_turn, false),
    ...normalizeCheckedItems(checkInput.conversation_history, true),
  ];
};

export const getGuardrailCheckedTargets = (data: GuardrailCheckData): GuardrailCheckedTarget[] => {
  const items = getGuardrailCheckedItems(data);
  const targets: GuardrailCheckedTarget[] = [];
  const unmatchedToolCallIndexes: number[] = [];

  for (const item of items) {
    if (item.isHistory) {
      continue;
    }

    if (item.type !== GuardrailCheckedType.ToolCall && item.type !== GuardrailCheckedType.ToolResult) {
      targets.push({ type: item.type });
      continue;
    }

    if (item.type === GuardrailCheckedType.ToolCall) {
      targets.push({ type: item.type, toolName: item.toolName });
      unmatchedToolCallIndexes.push(targets.length - 1);
      continue;
    }

    const matchingIndexPosition = [...unmatchedToolCallIndexes]
      .map((targetIndex, indexPosition) => ({ targetIndex, indexPosition }))
      .reverse()
      .find(({ targetIndex }) => targets[targetIndex].toolName === item.toolName)?.indexPosition;
    if (matchingIndexPosition !== undefined) {
      const matchingTargetIndex = unmatchedToolCallIndexes[matchingIndexPosition];
      targets[matchingTargetIndex] = {
        type: GuardrailCheckedType.ToolResult,
        toolName: item.toolName || targets[matchingTargetIndex].toolName,
      };
      unmatchedToolCallIndexes.splice(matchingIndexPosition, 1);
      continue;
    }

    targets.push({ type: item.type, toolName: item.toolName });
  }

  const hasHistory = items.some(
    (item) =>
      item.isHistory && item.type !== GuardrailCheckedType.ToolCall && item.type !== GuardrailCheckedType.ToolResult
  );
  if (hasHistory) {
    targets.push({ type: GuardrailCheckedType.History });
  }

  return targets;
};
